import {
  textUI,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
  addressNoMilitaryUI,
  addressNoMilitarySchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { formatReviewDate } from 'platform/forms-system/src/js/helpers';

import { employmentAppliedFields } from '../definitions/constants';
import { HideDefaultDateHint } from '../helpers/dateHint';
import { validateNotOnOrBeforeDOB } from '../helpers/dobValidation';

/** @type {ArrayBuilderOptions} */
const options = {
  arrayPath: employmentAppliedFields.parentObject,
  nounSingular: 'employment application record',
  nounPlural: 'employment application records',
  required: false,
  isItemIncomplete: item =>
    !item?.[employmentAppliedFields.employerName] ||
    !item?.[employmentAppliedFields.typeOfWork] ||
    !item?.[employmentAppliedFields.employerAddress] ||
    !item?.[employmentAppliedFields.dateApplied],
  maxItems: 3,
  text: {
    getItemName: item =>
      item[employmentAppliedFields.employerName] || 'Employer',
    cardDescription: item => {
      const parts = [];

      if (item?.[employmentAppliedFields.typeOfWork]) {
        parts.push(item[employmentAppliedFields.typeOfWork]);
      }

      if (item?.[employmentAppliedFields.dateApplied]) {
        parts.push(
          `Applied: ${formatReviewDate(
            item[employmentAppliedFields.dateApplied],
          )}`,
        );
      }

      return parts.join(' • ');
    },
  },
};

export const employmentStatementHistoryOptions = options;

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    [employmentAppliedFields.hasTriedEmployment]: arrayBuilderYesNoUI(options, {
      title:
        'Have you applied for employment since you became too disabled to work?',
      hint: 'If you select Yes, we’ll ask about the jobs you applied for. You can add up to 3 employment application records.',
      errorMessages: {
        required:
          'Please select "Yes" if you have at least one employment application record to add.',
      },
    }),
  },
  schema: {
    type: 'object',
    properties: {
      [employmentAppliedFields.hasTriedEmployment]: arrayBuilderYesNoSchema,
    },
    required: [employmentAppliedFields.hasTriedEmployment],
  },
};

/** @returns {PageSchema} */
const employmentInformationPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: 'Employment application details',
      nounSingular: options.nounSingular,
    }),
    [employmentAppliedFields.employerName]: textUI({
      title: 'Name of employer',
      errorMessages: {
        required: 'Enter the employer name, for example, Local Grocery Store',
        pattern: 'Enter the employer name, for example, Local Grocery Store',
      },
    }),
    [employmentAppliedFields.employerAddress]: addressNoMilitaryUI({
      title: 'Employer address',
      omit: ['street3'],
    }),
    [employmentAppliedFields.typeOfWork]: textUI({
      title: 'Type of work you applied for',
      errorMessages: {
        required: 'Enter the kind of work you applied for at this employer',
        pattern: 'Enter the kind of work you applied for at this employer',
      },
    }),
    [employmentAppliedFields.dateApplied]: {
      ...currentOrPastDateUI({
        title: 'Date you applied for employment',
        hint: 'For example: January 19 2022',
        errorMessages: {
          required:
            'Enter the date (month, day, and 4-digit year) you applied to work for this employer',
          pattern:
            'Enter the date (month, day, and 4-digit year) you applied to work for this employer',
        },
        validations: [
          validateNotOnOrBeforeDOB(
            "Enter a valid date. The date you applied for employment cannot be on or before the Veteran's date of birth.",
          ),
        ],
        useAllFormData: true,
      }),
      'ui:description': HideDefaultDateHint,
    },
  },
  schema: {
    type: 'object',
    properties: {
      [employmentAppliedFields.employerName]: {
        type: 'string',
        maxLength: 100,
      },
      [employmentAppliedFields.employerAddress]: addressNoMilitarySchema({
        omit: ['street3'],
      }),
      [employmentAppliedFields.typeOfWork]: {
        type: 'string',
        maxLength: 100,
      },
      [employmentAppliedFields.dateApplied]: currentOrPastDateSchema,
    },
    required: [
      employmentAppliedFields.employerName,
      employmentAppliedFields.employerAddress,
      employmentAppliedFields.typeOfWork,
      employmentAppliedFields.dateApplied,
    ],
  },
};

export default arrayBuilderPages(options, pageBuilder => ({
  employmentHistorySummary: pageBuilder.summaryPage({
    title: 'Review your employment application records',
    path: 'employment-application-records-summary',
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  employmentHistoryPage: pageBuilder.itemPage({
    title: 'Employment application record',
    path: 'employment-application-record/:index/basic-info',
    uiSchema: employmentInformationPage.uiSchema,
    schema: employmentInformationPage.schema,
  }),
}));
